/**
 * Text highlighting operations
 */

import { getUniqueId } from '@/lib/commonHelpers';
import {
  getValidContainers,
  isWithinEditableElement,
  isWithinTableElement,
} from '@/pages/experiences/hooks/utils/textSelectionHelper';
import { useRealtimeExperienceStore } from '@/pages/experiences/store/realtimeExperienceStore';
import {
  HIGHLIGHT_SCOPE_SELECTOR,
  createActionButtons,
  createMinimizedNote,
  createNoteEditor,
  safeRemoveMinimizedNote,
} from './uiHelpers';

const ALLOWED_TAGS = ['U', 'STRONG', 'I', 'A', 'SUB', 'SUP', 'SPAN'];

/**
 * v4 fill-gap blanks (fill-text spans, dropdown triggers, in-gap math
 * editors, data-gap-id) AND hot-text chips (data-hot-id) are atomic — like
 * v3's .inline-text-input / .inline-dropdown-input divs they count as ONE
 * character and their React-owned innards are never traversed, so saved
 * offsets stay stable while the student edits answers (R2-G55) and a
 * chip re-render (answer toggle / show-correct) never clobbers a wrapped
 * highlight span.
 */
const isAtomicGapElement = (node: any): boolean =>
  node?.nodeType === Node.ELEMENT_NODE &&
  typeof node.hasAttribute === 'function' &&
  (node.hasAttribute('data-gap-id') || node.hasAttribute('data-hot-id'));

// Offset walks skip accessory subtrees so minimized note/button text isn't counted.
// Class fallback covers accessories not yet stamped with the attribute.
const ACCESSORY_CLASS_NAMES = [
  'minimized-note-display',
  'highlight-buttons-wrapper',
  'image-highlight-buttons-wrapper',
];
const isHighlightAccessory = (node: any): boolean =>
  node?.nodeType === Node.ELEMENT_NODE &&
  (node.hasAttribute?.('data-highlight-accessory') ||
    ACCESSORY_CLASS_NAMES.some((className) => node.classList?.contains(className)));
/** Group main span first, then a non-member span for simple highlights. */
export const findMainSpan = (root: ParentNode, highlightId: string): HTMLElement | null => {
  const id = CSS.escape(highlightId);
  return (root.querySelector(`.highlighted-text[data-span-group="${id}"]`) ??
    root.querySelector(
      `.highlighted-text[data-highlight-id="${id}"]:not([data-span-group-member])`
    )) as HTMLElement | null;
};

// The one atomic-token list. The offset walks count these as a single unit and
// never descend into them, so a second copy would corrupt stored offsets.
export const isAtomicInlineToken = (node: any): boolean => {
  if (isAtomicGapElement(node)) return true;
  if (node?.nodeType !== Node.ELEMENT_NODE) return false;
  return (
    node.nodeName === 'IMG' ||
    node.nodeName === 'MATH' ||
    (node.nodeName === 'SPAN' && node.classList?.contains('math-input')) ||
    (node.nodeName === 'DIV' &&
      (node.classList?.contains('inline-dropdown-input') ||
        node.classList?.contains('inline-text-input')))
  );
};

const isOpaqueToOffsets = (node: any): boolean =>
  isHighlightAccessory(node) || isAtomicInlineToken(node);

const hasOpaqueAncestor = (node: Node, container: Element): boolean => {
  let current: Node | null = node.parentNode;
  while (current && current !== container) {
    if (isOpaqueToOffsets(current)) return true;
    current = current.parentNode;
  }
  return false;
};

export const clampRangeToContainerText = (range: Range, container: Element): Range | null => {
  if (!range.intersectsNode(container)) return null;

  const clamped = range.cloneRange();
  const scope = document.createRange();
  scope.selectNodeContents(container);
  // Boundaries outside the container come to its edge first, so the walks below
  // stay inside this container and cannot reach a pinned second copy.
  if (clamped.compareBoundaryPoints(Range.START_TO_START, scope) < 0) {
    clamped.setStart(container, 0);
  }
  if (clamped.compareBoundaryPoints(Range.END_TO_END, scope) > 0) {
    clamped.setEnd(container, container.childNodes.length);
  }

  const startIsText = clamped.startContainer.nodeType === Node.TEXT_NODE;
  const endIsText = clamped.endContainer.nodeType === Node.TEXT_NODE;
  if (!startIsText || !endIsText) {
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
    let first: Text | null = null;
    let last: Text | null = null;
    while (walker.nextNode()) {
      const textNode = walker.currentNode as Text;
      if (!textNode.data.trim()) continue;
      if (hasOpaqueAncestor(textNode, container)) continue;
      if (!clamped.intersectsNode(textNode)) continue;
      if (!first) first = textNode;
      last = textNode;
    }
    // The drag covered no text of its own inside this container.
    if (!first || !last) return null;
    if (!startIsText) clamped.setStart(first, 0);
    if (!endIsText) clamped.setEnd(last, last.data.length);
  }

  if (clamped.collapsed || !clamped.toString().trim()) return null;
  return clamped;
};

const TOOLTIP_CONFIG = {
  width: 230,
  height: 200,
  xOffset: 0,
  yOffset: 88,
  fallbackY: 20,
};

/**
 * Safely remove a DOM element with proper checks
 */
const safeRemoveElement = (element: Element | null | undefined) => {
  if (!element) return;
  try {
    if (document.body.contains(element)) {
      element.remove();
    }
  } catch (error) {
    console.warn('Error removing element:', error);
  }
};

/**
 * Find text node at given offset
 */
const findTextNode = (node: any, offset: number) => {
  let currentOffset = offset;
  const stack = [node];

  while (stack.length > 0) {
    const current = stack.pop();

    if (current.nodeType === Node.TEXT_NODE) {
      if (currentOffset <= current.length) {
        return { node: current, offset: currentOffset };
      }
      currentOffset -= current.length;
    } else if (current.nodeType === Node.ELEMENT_NODE) {
      // Accessory UI counts as 0 characters (don't traverse children)
      if (isHighlightAccessory(current)) {
        continue;
      }

      // Atomic tokens count as 1 character; never traverse their children.
      if (isAtomicInlineToken(current)) {
        currentOffset -= 1;
        continue;
      }

      // For other elements, traverse their children
      for (let i = current.childNodes.length - 1; i >= 0; i--) {
        stack.push(current.childNodes[i]);
      }
    }
  }
  return null;
};

/**
 * Get absolute text offset from container
 */
const getTextOffset = (container: any, offset: number): number => {
  let textOffset = 0;

  const traverse = (node: any): boolean => {
    if (node === container) {
      textOffset += offset;
      return true;
    }

    if (node.nodeType === Node.TEXT_NODE) {
      textOffset += node.textContent.length;
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      // Accessory UI counts as 0 characters (don't traverse children)
      if (isHighlightAccessory(node)) {
        // skip subtree
      }
      // Atomic tokens count as 1 character; never traverse their children.
      else if (isAtomicInlineToken(node)) {
        textOffset += 1;
      }
      // Traverse children for other elements
      else {
        for (const child of node.childNodes) {
          if (traverse(child)) return true;
        }
      }
    }
    return false;
  };

  let parentNode = container.parentNode;
  while (
    parentNode?.nodeType === Node.ELEMENT_NODE &&
    !parentNode.classList?.contains('show-html') &&
    !parentNode.classList?.contains('show-fill-answer')
  ) {
    parentNode = parentNode.parentNode;
  }

  traverse(parentNode || container.parentNode);
  return textOffset;
};

/**
 * Check if selection is within existing highlight
 */
const isWithinHighlight = (range: any): boolean => {
  for (let node = range.commonAncestorContainer; node; node = node.parentNode) {
    if (node.nodeType === Node.ELEMENT_NODE && node.classList?.contains('highlighted-text')) {
      return true;
    }
  }
  return false;
};

// Subtree text length excluding accessories — otherwise the minimized note
// inflates the span end and falsely blocks a selection right after a noted highlight.
const getHighlightedTextLength = (node: any): number => {
  if (node.nodeType === Node.TEXT_NODE) return node.textContent.length;
  if (node.nodeType !== Node.ELEMENT_NODE || isHighlightAccessory(node)) return 0;
  if (isAtomicInlineToken(node)) return 1;
  let length = 0;
  node.childNodes.forEach((child: any) => {
    length += getHighlightedTextLength(child);
  });
  return length;
};

/**
 * Check if selection overlaps with existing highlights
 */
const isOverlapping = (startOffset: number, endOffset: number, container: HTMLElement): boolean => {
  const spans = container.querySelectorAll('.highlighted-text');
  return Array.from(spans).some((span: any) => {
    const spanStart = getTextOffset(span.firstChild, 0);
    const spanEnd = spanStart + getHighlightedTextLength(span);
    return (
      (startOffset >= spanStart && startOffset < spanEnd) ||
      (endOffset > spanStart && endOffset <= spanEnd) ||
      (startOffset <= spanStart && endOffset >= spanEnd)
    );
  });
};

/**
 * Create highlight span element
 */
const createHighlightSpan = (highlight: any): HTMLSpanElement => {
  const span = document.createElement('span');
  span.className = 'highlighted-text';
  span.dataset.highlightId = highlight.id;
  span.dataset.hasNote = highlight.note?.trim() ? 'true' : 'false';
  Object.assign(span.style, {
    backgroundColor: highlight.color,
    position: 'relative',
    padding: '2px 0',
    borderRadius: '2px',
    transition: 'opacity 0.2s',
    display: 'inline',
  });
  return span;
};

/**
 * Copy content to highlight span
 * NOTE: Math elements (<math> and .math-input) are NOT copied - they are handled separately via cross-element highlighting
 */
const copyContentToSpan = (node: any, span: HTMLElement, highlight: any): void => {
  if (node.nodeType === Node.TEXT_NODE) {
    span.appendChild(document.createTextNode(node.textContent));
  } else if (node.nodeType === Node.ELEMENT_NODE) {
    // CRITICAL: Skip <math> elements (MathML) and .math-input elements
    // They should never be inside a highlight span to preserve formulas
    if (node.tagName && node.tagName.toUpperCase() === 'MATH') {
      console.warn(
        '⚠️ Attempted to copy <math> element into highlight span - SKIPPING to preserve formula'
      );
      return; // Do not copy math elements
    }

    if (node.classList && node.classList.contains('math-input')) {
      console.warn(
        '⚠️ Attempted to copy .math-input element into highlight span - SKIPPING to preserve formula'
      );
      return; // Do not copy math-input elements
    }

    if (node.tagName === 'BR') {
      span.appendChild(node.cloneNode(true));
      return;
    }
    // DIV elements (like inline inputs) should be included
    if (node.tagName === 'DIV') {
      span.appendChild(node.cloneNode(true));
    } else if (ALLOWED_TAGS.includes(node.tagName)) {
      Object.assign(node.dataset, {
        originalBackgroundColor: node.style.backgroundColor,
        highlightId: highlight.id,
      });
      node.style.backgroundColor = '';
      span.appendChild(node.cloneNode(true));
    } else {
      node.childNodes.forEach((child: any) => copyContentToSpan(child, span, highlight));
    }
  }
};

/**
 * Check if selection spans across different block-level elements
 *
 * PURPOSE: Determine if a text selection crosses block boundaries (e.g., from one <p> to another <li>)
 * This helps us decide which highlighting strategy to use:
 * - Simple selection (within one block): Use single span wrapper
 * - Cross-element selection (across blocks): Use multiple linked spans (span groups)
 *
 * EXAMPLE:
 * Simple: "This is text" within <p>This is text</p>
 * Cross-element: "end of para" to "start of list" across <p> and <li> tags
 *
 * @param range - The text selection range
 * @returns true if selection crosses block boundaries, false otherwise
 */
const isCrossElementSelection = (range: Range): boolean => {
  // Get the start and end containers
  let startContainer = range.startContainer;
  let endContainer = range.endContainer;

  // If they're text nodes, get their parent elements
  // (Text nodes don't have tagName, so we need their parent element)
  if (startContainer.nodeType === Node.TEXT_NODE) {
    startContainer = startContainer.parentElement!;
  }
  if (endContainer.nodeType === Node.TEXT_NODE) {
    endContainer = endContainer.parentElement!;
  }

  // Define block-level elements that should trigger cross-element mode
  // These are HTML elements that create new blocks/lines in the layout
  const blockElements = [
    'P', // Paragraphs
    'DIV', // Divisions
    'LI', // List items
    'H1',
    'H2',
    'H3',
    'H4',
    'H5',
    'H6', // Headings
    'TD',
    'TH', // Table cells
    'BLOCKQUOTE', // Block quotes
  ];

  // Find the closest block-level parent for start
  let startBlock: Element | null = startContainer as Element;
  while (startBlock && !blockElements.includes(startBlock.tagName)) {
    startBlock = startBlock.parentElement;
  }

  // Find the closest block-level parent for end
  let endBlock: Element | null = endContainer as Element;
  while (endBlock && !blockElements.includes(endBlock.tagName)) {
    endBlock = endBlock.parentElement;
  }

  // If they're in different block elements, it's a cross-element selection
  const isCross = startBlock !== endBlock && startBlock !== null && endBlock !== null;

  return isCross;
};

/**
 * Get all text nodes within a range, excluding text inside Math elements
 *
 * PURPOSE: Extract all text nodes that are part of the selection
 * This is crucial for cross-element highlighting where we need to wrap
 * each text segment individually while preserving the DOM structure
 *
 * CRITICAL: Text nodes inside <math> elements MUST NOT be included
 * because wrapping them would destroy the mathematical formulas
 *
 * @param range - The text selection range
 * @returns Array of text nodes that intersect with the range (excluding math content)
 */
const getTextNodesInRange = (range: Range): Text[] => {
  const textNodes: Text[] = [];

  // TreeWalker efficiently traverses only text nodes in the DOM
  const treeWalker = document.createTreeWalker(
    range.commonAncestorContainer,
    NodeFilter.SHOW_TEXT, // Only show text nodes
    null
  );

  let currentNode: Node | null;
  while ((currentNode = treeWalker.nextNode())) {
    // Only include text nodes that actually intersect with our selection range
    if (range.intersectsNode(currentNode)) {
      const textNode = currentNode as Text;

      // CRITICAL: Check if this text node is inside a <math> element or math-input
      // If so, skip it to preserve the mathematical formula
      let parent = textNode.parentNode;
      let isInsideMath = false;

      while (parent && parent !== range.commonAncestorContainer) {
        if (parent.nodeType === Node.ELEMENT_NODE) {
          const element = parent as Element;
          // v4 fill-gap blanks are atomic (offset = 1 char) and React-owned —
          // never wrap their inner text (a span inside the gap would corrupt
          // offsets and be clobbered on the next React commit).
          if (isAtomicGapElement(element)) {
            isInsideMath = true;
            break;
          }
          // Check for <math> tag (MathML) OR math-input class
          if (
            element.tagName.toUpperCase() === 'MATH' ||
            element.classList.contains('math-input')
          ) {
            isInsideMath = true;
            const elementType = element.tagName.toUpperCase() === 'MATH' ? '<math>' : '.math-input';
            console.log(
              `  ⚠️ Skipping text node inside ${elementType} element:`,
              textNode.textContent?.substring(0, 20)
            );
            break;
          }
        }
        parent = parent.parentNode;
      }

      // Only include text nodes that are NOT inside math elements
      if (!isInsideMath) {
        textNodes.push(textNode);
      }
    }
  }

  return textNodes;
};

/**
 * 🎯 CORE FUNCTION: Wrap range in highlight spans while preserving DOM structure
 *
 * PURPOSE: Handle cross-element selections (selections that span across different block elements)
 *
 * STRATEGY: Create multiple individual <span> elements (one for each text segment) and link them
 * together as a "span group". This preserves the original DOM structure while still treating
 * the entire selection as a single logical highlight.
 *
 * EXAMPLE INPUT:
 * <p>This is paragraph text</p>
 * <ol><li>This is list text</li></ol>
 *
 * User selects: "paragraph text" to "list text"
 *
 * EXAMPLE OUTPUT:
 * <p>This is <span data-span-group="id123">paragraph text</span></p>
 * <ol><li><span data-span-group-member="id123">This is list text</span></li></ol>
 *
 * BENEFITS:
 * - Preserves original HTML structure (line breaks, formatting, etc.)
 * - No impact on existing text formatting
 * - UI elements (buttons, notes) only on first span
 * - All spans react to hover as a unified group
 *
 * @param range - The text selection range
 * @param highlight - Highlight data (id, color, note, etc.)
 * @param onUpdate - Callback to update highlight state
 * @param translate - Translation function
 * @param commentsVisible - Whether comments/notes should be visible
 */
const wrapRangeInHighlight = (
  range: Range,
  highlight: any,
  onUpdate: (highlight: any, action?: string) => void,
  translate: any,
  commentsVisible: boolean
) => {
  try {
    // STEP 1: Get all text nodes that intersect with the selection range
    const textNodes = getTextNodesInRange(range);

    if (textNodes.length === 0) {
      console.warn('No text nodes found in range for cross-element highlight');
      return;
    }

    console.log(`📝 wrapRangeInHighlight: Found ${textNodes.length} text nodes to wrap`);
    textNodes.forEach((node, idx) => {
      const preview = (node.textContent || '').substring(0, 20);
      const parent = node.parentNode;
      const parentTag =
        parent && parent.nodeType === Node.ELEMENT_NODE ? (parent as Element).tagName : 'unknown';
      console.log(`   Text node ${idx + 1}: "${preview}..." in <${parentTag}>`);

      // Check for image siblings
      if (parent) {
        const siblings = Array.from(parent.childNodes);
        const hasImgSibling = siblings.some(
          (n) => n.nodeType === Node.ELEMENT_NODE && (n as Element).tagName === 'IMG'
        );
        if (hasImgSibling) {
          console.log(`   ⚠️  Parent has IMG sibling - will preserve carefully`);
        }
      }
    });

    // Array to store all created spans (these form the "span group")
    const allSpans: HTMLElement[] = [];

    // STEP 2: Process each text node and wrap it in its own highlight span
    // Process in REVERSE order to avoid issues with DOM mutations affecting node positions
    for (let i = textNodes.length - 1; i >= 0; i--) {
      const textNode = textNodes[i];

      // CRITICAL: Verify parent exists before processing
      // This ensures we never accidentally affect orphaned nodes
      const parent = textNode.parentNode;
      if (!parent) {
        console.warn('⚠️  Text node has no parent, skipping');
        continue;
      }

      // STEP 2a: Determine which portion of this text node is actually selected
      let startOffset = 0;
      let endOffset = textNode.length;

      // If this is the first text node, use the selection's start offset
      if (textNode === range.startContainer) {
        startOffset = range.startOffset;
      }
      // If this is the last text node, use the selection's end offset
      if (textNode === range.endContainer) {
        endOffset = range.endOffset;
      }

      // Skip if no content to highlight (edge case)
      if (startOffset >= endOffset) {
        continue;
      }

      // Extract the text to highlight
      const textToHighlight = textNode.textContent?.substring(startOffset, endOffset) || '';
      if (!textToHighlight.trim()) {
        continue; // Skip empty text nodes
      }

      // STEP 2b: Split the text node to isolate the selected portion
      let nodeToWrap: Text = textNode;

      // Log before splitting to debug Math conversion issue
      const parentEl = textNode.parentNode;
      if (parentEl) {
        const mathElsBefore = (parentEl as HTMLElement).querySelectorAll('math').length;
        const mathInputsBefore = (parentEl as HTMLElement).querySelectorAll('.math-input').length;
        console.log(
          `  Before split - Parent has ${mathElsBefore} <math> + ${mathInputsBefore} .math-input element(s)`
        );
        console.log(
          `  Before split - Parent innerHTML:`,
          (parentEl as HTMLElement).innerHTML?.substring(0, 150)
        );
      }

      // Split after the selection if there's text after it
      if (endOffset < textNode.length) {
        textNode.splitText(endOffset);
      }

      // Split before the selection if there's text before it
      if (startOffset > 0) {
        nodeToWrap = textNode.splitText(startOffset);
      }

      // Log after splitting to see if math elements are affected
      if (parentEl) {
        const mathElsAfter = (parentEl as HTMLElement).querySelectorAll('math').length;
        const mathInputsAfter = (parentEl as HTMLElement).querySelectorAll('.math-input').length;
        console.log(
          `  After split - Parent has ${mathElsAfter} <math> + ${mathInputsAfter} .math-input element(s)`
        );
        console.log(
          `  After split - Parent innerHTML:`,
          (parentEl as HTMLElement).innerHTML?.substring(0, 150)
        );

        if (mathElsAfter === 0 && mathInputsAfter === 0 && parentEl) {
          console.error('❌ MATH ELEMENTS DISAPPEARED AFTER SPLIT!');
          // Check if it got converted to text
          const parentHTML = (parentEl as HTMLElement).innerHTML;
          if (parentHTML.includes('²') || parentHTML.includes('√') || /\d+\/\d+/.test(parentHTML)) {
            console.error('❌ Math formula converted to plain text/symbols!');
          }
        }
      }

      // STEP 2c: Create highlight span and wrap the selected text
      const span = createHighlightSpan(highlight);

      // CRITICAL: Use textContent (not innerHTML) to preserve text-only content
      // This ensures images are never copied into the span
      span.textContent = nodeToWrap.textContent;

      // Replace the text node with our new highlight span
      // IMPORTANT: Only replace the text node, never touch sibling nodes (especially images!)
      if (nodeToWrap.parentNode) {
        const parentNode = nodeToWrap.parentNode;

        // EXTRA SAFETY: Before replacing, verify no image will be affected
        // Check siblings before and after to ensure they stay untouched
        const nextSibling = nodeToWrap.nextSibling;
        const prevSibling = nodeToWrap.previousSibling;

        // Replace the text node with the highlight span
        parentNode.replaceChild(span, nodeToWrap);

        // VERIFY: Images are still in place after replacement
        if (
          nextSibling &&
          nextSibling.nodeType === Node.ELEMENT_NODE &&
          (nextSibling as Element).tagName === 'IMG'
        ) {
          if (span.nextSibling !== nextSibling) {
            console.error('❌ IMAGE WAS MOVED/REMOVED! Attempting to fix...');
            // Image was moved, try to restore it
            if (nextSibling.parentNode !== parentNode) {
              parentNode.insertBefore(nextSibling, span.nextSibling);
            }
          }
        }

        if (
          prevSibling &&
          prevSibling.nodeType === Node.ELEMENT_NODE &&
          (prevSibling as Element).tagName === 'IMG'
        ) {
          if (span.previousSibling !== prevSibling) {
            console.error('❌ IMAGE WAS MOVED/REMOVED! Attempting to fix...');
            // Image was moved, try to restore it
            if (prevSibling.parentNode !== parentNode) {
              parentNode.insertBefore(prevSibling, span);
            }
          }
        }

        // Additional check for <math> and .math-input elements
        if (nextSibling && nextSibling.nodeType === Node.ELEMENT_NODE) {
          const nextEl = nextSibling as Element;
          const isMath = nextEl.tagName && nextEl.tagName.toUpperCase() === 'MATH';
          const isMathInput = nextEl.classList && nextEl.classList.contains('math-input');

          if (isMath || isMathInput) {
            const elementType = isMath ? '<math>' : '.math-input';
            console.log(`✅ ${elementType} element detected after span - verifying preservation`);
            if (span.nextSibling === nextSibling) {
              console.log(`✅ ${elementType} after span IS preserved correctly`);
            } else {
              console.error(`❌ ${elementType} after span was LOST!`);
            }
          }
        }

        if (prevSibling && prevSibling.nodeType === Node.ELEMENT_NODE) {
          const prevEl = prevSibling as Element;
          const isMath = prevEl.tagName && prevEl.tagName.toUpperCase() === 'MATH';
          const isMathInput = prevEl.classList && prevEl.classList.contains('math-input');

          if (isMath || isMathInput) {
            const elementType = isMath ? '<math>' : '.math-input';
            console.log(`✅ ${elementType} element detected before span - verifying preservation`);
            if (span.previousSibling === prevSibling) {
              console.log(`✅ ${elementType} before span IS preserved correctly`);
            } else {
              console.error(`❌ ${elementType} before span was LOST!`);
            }
          }
        }

        allSpans.unshift(span); // Add to beginning to maintain document order
      }
    }

    // STEP 3: Set up the "span group" system - link all spans as one logical highlight
    if (allSpans.length > 0) {
      const firstSpan = allSpans[0];

      // STEP 3a: Add UI components (buttons, notes) ONLY to the first span
      // This prevents duplicate UI elements while still allowing all spans to show them
      updateTextNoteUI(firstSpan, highlight, onUpdate, translate, commentsVisible);

      // STEP 3b: Mark this as a "span group" by storing the group ID
      // The first span gets "data-span-group" to identify it as the main span
      firstSpan.dataset.spanGroup = highlight.id;

      // STEP 3c: Set up shared hover behavior across all spans in the group
      // This timeout is shared across all span hover events to prevent flickering
      // when moving the mouse between different spans in the same group
      let hideTimeout: NodeJS.Timeout | null = null;

      // STEP 3d: Add hover listeners to ALL spans in the group (including first span)
      allSpans.forEach((span, index) => {
        // Mark member spans (all except first) with "data-span-group-member"
        // This helps us identify them later and prevent adding UI to them
        if (index > 0) {
          span.dataset.spanGroupMember = highlight.id;
        }

        // MOUSEENTER: Show buttons and increase note opacity when hovering ANY span
        span.addEventListener('mouseenter', () => {
          // Cancel any pending hide timeout to prevent premature hiding
          // This is critical when moving mouse between spans
          if (hideTimeout) {
            clearTimeout(hideTimeout);
            hideTimeout = null;
          }

          // Get UI elements from the FIRST span (they're only there)
          const buttonWrapper = firstSpan.querySelector('.highlight-buttons-wrapper');
          const minimizedNote = firstSpan.querySelector('.minimized-note-display');

          // Show buttons when hovering any span in the group
          if (buttonWrapper) {
            (buttonWrapper as HTMLElement).style.display = 'flex';
          }

          // Increase note opacity for better visibility
          if (minimizedNote) {
            (minimizedNote as HTMLElement).style.opacity = '1';
          }
        });

        // MOUSELEAVE: Hide buttons after a delay (if not hovering any span or buttons)
        span.addEventListener('mouseleave', () => {
          const buttonWrapper = firstSpan.querySelector('.highlight-buttons-wrapper');
          const minimizedNote = firstSpan.querySelector('.minimized-note-display');

          // Clear any existing timeout to avoid multiple timeouts running
          if (hideTimeout) {
            clearTimeout(hideTimeout);
          }

          // Set new timeout to hide buttons/reset note opacity
          // The delay allows smooth transitions when moving between spans
          hideTimeout = setTimeout(() => {
            // Check if mouse is still hovering ANY span in the group
            const isHoveringGroup = allSpans.some((s) => s.matches(':hover'));
            // Also check if hovering the buttons themselves
            const isHoveringButtons = buttonWrapper?.matches(':hover');

            // Only hide if not hovering any span or the buttons
            if (!isHoveringGroup && !isHoveringButtons) {
              if (buttonWrapper) {
                (buttonWrapper as HTMLElement).style.display = 'none';
              }
              if (minimizedNote) {
                (minimizedNote as HTMLElement).style.opacity = '0.5';
              }
            }
            hideTimeout = null;
          }, 100); // 100ms delay for smooth UX
        });
      });
    } else {
      console.warn('No spans created for cross-element highlight');
    }
  } catch (error) {
    console.error('Error wrapping range in highlight:', error);
  }
};

/**
 * Apply text highlights from saved data
 */
export const applyTextHighlights = (
  highlights: any[],
  highlightsIdentifier: string,
  htmlRef: HTMLElement,
  onUpdate: (highlight: any, action?: string) => void,
  translate: any,
  commentsVisible: boolean
) => {
  console.log(`🔄 Applying ${highlights.length} saved highlights`);

  // Store original HTML for comparison
  const originalHTML = htmlRef.innerHTML;
  const originalImages = htmlRef.querySelectorAll('img').length;
  console.log(`   Original HTML has ${originalImages} images`);

  highlights.forEach((highlight, idx) => {
    console.log(`\n--- Applying highlight ${idx + 1}/${highlights.length} ---`);
    console.log(`   Offsets: ${highlight.startOffset} to ${highlight.endOffset}`);

    const selection = window.getSelection();
    selection?.removeAllRanges();

    const start = findTextNode(htmlRef, highlight.startOffset);
    const end = findTextNode(htmlRef, highlight.endOffset);

    if (start && end) {
      try {
        const range = document.createRange();
        range.setStart(start.node, start.offset);
        range.setEnd(end.node, end.offset);

        console.log(`   Range text: "${range.toString().substring(0, 50)}..."`);
        console.log(`   Range HTML:`, range.cloneContents());

        // Don't add to selection - just create the highlight directly
        // This prevents DOM manipulation issues with images
        createTextHighlight(
          range,
          highlight,
          highlightsIdentifier,
          onUpdate,
          translate,
          commentsVisible,
          true
        );

        // Clear selection after each highlight to prevent interference
        selection?.removeAllRanges();

        // Check if images are still there after this highlight
        const imagesNow = htmlRef.querySelectorAll('img').length;
        if (imagesNow < originalImages) {
          console.error(`❌ IMAGES LOST! Was ${originalImages}, now ${imagesNow}`);
        }
      } catch (e) {
        console.error('Error applying highlight:', e);
      }
    } else {
      console.warn('Could not find start/end nodes for highlight');
    }
  });

  const finalImages = htmlRef.querySelectorAll('img').length;
  console.log(
    `\n✅ Finished applying highlights. Final images: ${finalImages} (started with ${originalImages})`
  );
};

/**
 * Create new text highlight from selection
 */
export const createTextHighlight = (
  range: Range,
  existingHighlight: any | null,
  highlightsIdentifier: string,
  onUpdate: (highlight: any, action?: string) => void,
  translate: any,
  commentsVisible: boolean,
  isApplying = false
) => {
  // Skip highlighting if highlightsIdentifier doesn't exist
  if (!highlightsIdentifier && !isApplying) {
    console.log('⏭️ Skipping text highlight creation - no highlightsIdentifier provided');
    return;
  }

  // Validate selection
  const selectedText = range.toString().trim();

  // Skip if selection is too short
  if (selectedText.length < 1) {
    console.log('Selection too short, skipping highlight');
    return;
  }

  // Skip if already within a highlight
  if (isWithinHighlight(range)) {
    console.log('Selection within existing highlight, skipping');
    return;
  }

  // Skip if an endpoint sits INSIDE an atomic element (gap blank / hot-text
  // chip) — its React-owned text is not offset-counted, so a mark anchored
  // there cannot round-trip.
  for (const endpoint of [range.startContainer, range.endContainer]) {
    let node: Node | null = endpoint;
    while (node && node !== document.body) {
      if (isAtomicGapElement(node)) {
        console.log('Selection endpoint inside an atomic gap/chip element, skipping highlight');
        return;
      }
      node = node.parentNode;
    }
  }

  // NOTE: We now ALLOW images within text selections
  // Images will be included in the highlight span but won't get the background color
  // This ensures text before and after images can be highlighted together

  const startOffset = getTextOffset(range.startContainer, range.startOffset);
  const endOffset = getTextOffset(range.endContainer, range.endOffset);

  if (endOffset - startOffset < 1) {
    console.log('Offset difference too small, skipping highlight');
    return;
  }

  // Check overlapping only for parent element
  let parentElement = range.commonAncestorContainer;
  if (parentElement.nodeType === Node.TEXT_NODE) {
    parentElement = parentElement.parentElement!;
  }

  // Find the closest valid container for overlap checking
  let checkElement = parentElement as HTMLElement;
  while (
    checkElement &&
    !checkElement.classList?.contains('show-html') &&
    !checkElement.classList?.contains('show-fill-answer')
  ) {
    if (checkElement.parentElement) {
      checkElement = checkElement.parentElement;
    } else {
      break;
    }
  }

  if (isOverlapping(startOffset, endOffset, checkElement)) {
    console.log('Selection overlaps with existing highlight, skipping');
    return;
  }

  // Create highlight data object
  const selectedColor = useRealtimeExperienceStore.getState().selectedHighlightColor;
  const highlight = {
    id: existingHighlight?.id || getUniqueId(),
    startOffset,
    endOffset,
    highlight_identifier: highlightsIdentifier,
    color: existingHighlight?.color ?? selectedColor,
    note: existingHighlight?.note ?? '',
  };

  // ========== DUAL STRATEGY APPROACH ==========
  // We use different highlighting strategies based on the selection type

  // STEP 1: Check if selection contains images or Math elements
  const clonedContent = range.cloneContents();
  const images = clonedContent.querySelectorAll('img');
  const mathElements = clonedContent.querySelectorAll('math'); // Check for <math> elements (MathML)
  const mathInputs = clonedContent.querySelectorAll('.math-input'); // Check for .math-input elements
  const containsImages = images.length > 0;
  const containsMath = mathElements.length > 0 || mathInputs.length > 0;
  // v4 fill-gap blanks and hot-text chips force the cross-element strategy
  // (like math): the simple strategy's deleteContents would rip the
  // React-owned element out of the DOM. Cross-element wraps only the
  // surrounding text nodes.
  const containsGaps = clonedContent.querySelectorAll('[data-gap-id], [data-hot-id]').length > 0;

  // Debug: Log image and Math information when applying saved highlights
  if (isApplying && (containsImages || containsMath)) {
    console.log(
      '🔄 Applying saved highlight - range contains',
      images.length,
      'image(s),',
      mathElements.length,
      '<math> element(s), and',
      mathInputs.length,
      '.math-input element(s)'
    );
    console.log('   Range:', range.toString().substring(0, 50) + '...');
    if (containsMath) {
      console.log(
        '   ⚠️ CONTAINS MATH ELEMENTS - will use cross-element strategy to preserve formulas'
      );
    }
  }

  // STEP 2: Determine if this is a simple or cross-element selection
  // Treat selections with images OR Math elements as cross-element to properly skip them
  const isCrossElement =
    isCrossElementSelection(range) || containsImages || containsMath || containsGaps;
  console.log(
    '🔍 Selection type:',
    isCrossElement ? 'Cross-element' : 'Simple',
    containsImages ? '(contains images)' : ''
  );

  if (isCrossElement) {
    // ========== STRATEGY A: Cross-Element Highlighting ==========
    // Used when selection spans across different block elements OR contains images
    //
    // APPROACH: Create multiple individual spans (span group) to preserve DOM structure
    // WHY: Single span wrapper would break block-level formatting, line breaks, and images
    //
    // RESULT: Multiple linked spans that behave as one logical highlight, images stay outside spans
    wrapRangeInHighlight(range, highlight, onUpdate, translate, commentsVisible);
  } else {
    // ========== STRATEGY B: Simple Highlighting ==========
    // Used when selection is within a single block element and contains no images
    //
    // APPROACH: Wrap entire selection in a single <span> element
    // WHY: Simpler, more efficient, preserves inline formatting well
    //
    // RESULT: Single span containing the highlighted text
    const span = createHighlightSpan(highlight);
    const selectedContent = range.cloneContents();
    selectedContent.childNodes.forEach((node: any) => copyContentToSpan(node, span, highlight));

    // Add UI components (buttons, note display)
    updateTextNoteUI(span, highlight, onUpdate, translate, commentsVisible);

    // Replace selection with our highlight span
    range.deleteContents();
    range.insertNode(span);
  }

  // Save highlight to store (skip if we're just applying saved highlights)
  if (!isApplying && !existingHighlight) {
    onUpdate(highlight);
  }

  // Clear selection to provide visual feedback
  if (isApplying) window.getSelection()?.removeAllRanges();
};

/**
 * Handle text selection and highlighting
 */
export const handleTextSelection = (
  highlightsIdentifier: string,
  onUpdate: (highlight: any, action?: string) => void,
  translate: any,
  commentsVisible: boolean,
  scopeEl?: HTMLElement | null
) => {
  // Skip highlighting if highlightsIdentifier doesn't exist
  // This prevents highlighting in segments where it shouldn't be enabled (like MCQs)
  if (!highlightsIdentifier) {
    console.log('⏭️ Skipping text highlight - no highlightsIdentifier provided');
    return;
  }

  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0 || selection.toString().trim().length === 0) return;

  // Check if within editable elements
  const range = selection.getRangeAt(0);
  let node: any = range.commonAncestorContainer;
  if (node.nodeType === Node.TEXT_NODE) node = node.parentNode;

  // Skip highlighting in editable elements
  if (isWithinEditableElement(node)) {
    return;
  }

  // Skip highlighting in table elements
  if (isWithinTableElement(node)) {
    return;
  }

  // Find valid highlight container - look for both specific containers and generic content containers
  const containers = [...new Set(getValidContainers())];

  // Intersection, not contains: a boundary dropped in the padding sits OUTSIDE
  // every container, and `contains` rejected the whole selection.
  const hits = containers.filter((container) => range.intersectsNode(container));
  // A selection spanning two unrelated containers still creates nothing.
  const spansUnrelated = hits.some((a) =>
    hits.some((b) => a !== b && !a.contains(b) && !b.contains(a))
  );

  let selectionCreated = false;
  if (hits.length > 0 && !spansUnrelated) {
    const target = hits[0];
    // With a document listener every instance hears every mouseup; only the one
    // owning this container may write, or a pinned copy duplicates the mark.
    // Closed by default: a missing scope must not grant ownership, or every
    // instance writes and a pinned copy duplicates the mark.
    const ownsTarget = !!scopeEl && (target === scopeEl || scopeEl.contains(target));
    const clamped = ownsTarget ? clampRangeToContainerText(range, target) : null;
    if (clamped) {
      try {
        createTextHighlight(
          clamped,
          null,
          highlightsIdentifier,
          onUpdate,
          translate,
          commentsVisible
        );
        selectionCreated = true;
      } catch (error) {
        console.warn('Error creating text highlight:', error);
      }
    }
  }

  // Clear selection after highlighting to provide visual feedback
  if (selectionCreated) {
    setTimeout(() => {
      window.getSelection()?.removeAllRanges();
    }, 100);
  }
};

/**
 * 🗑️ Remove text highlight (handles both single spans and span groups)
 *
 * PURPOSE: Completely remove a highlight and restore original text
 *
 * HANDLES TWO CASES:
 * 1. Simple Highlight: Single span - just unwrap and restore text
 * 2. Span Group (Cross-Element): Multiple linked spans - find all, remove all
 *
 * PROCESS:
 * 1. Find all spans that belong to this highlight (1 for simple, many for groups)
 * 2. Clean up UI elements (notes, buttons, tooltips)
 * 3. Unwrap each span and restore original text
 * 4. Update state to remove from store
 *
 * @param span - Any span that's part of the highlight (can be main or member)
 * @param highlightId - The unique ID of the highlight to remove
 * @param onUpdate - Callback to update highlight state
 */
export const removeTextHighlight = (
  span: HTMLElement,
  highlightId: string,
  onUpdate: (highlight: any, action: string) => void
) => {
  try {
    // STEP 1: Find all spans that are part of this highlight
    const allSpans: HTMLElement[] = [span];

    // Case A: This IS the main span (has data-span-group)
    if (span.dataset.spanGroup) {
      const groupId = span.dataset.spanGroup;
      // Find all member spans and add them
      document.querySelectorAll(`[data-span-group-member="${groupId}"]`).forEach((memberSpan) => {
        allSpans.push(memberSpan as HTMLElement);
      });
    }
    // Case B: This is a member span (has data-span-group-member)
    else if (span.dataset.spanGroupMember) {
      const groupId = span.dataset.spanGroupMember;
      // Find the main span first
      const mainSpan = document.querySelector(`[data-span-group="${groupId}"]`);
      if (mainSpan) {
        allSpans.length = 0; // Clear array and rebuild with main span first
        allSpans.push(mainSpan as HTMLElement);
        // Then add all member spans
        document.querySelectorAll(`[data-span-group-member="${groupId}"]`).forEach((memberSpan) => {
          allSpans.push(memberSpan as HTMLElement);
        });
      }
    }
    // Case C: Simple highlight (no group) - allSpans already has just this one span

    // Clean up notes and tooltips (only from the first span)
    const firstSpan = allSpans[0];
    const note = firstSpan.querySelector('.minimized-note-display');
    if (note) safeRemoveElement(note);

    // Remove note editing tooltips
    document.querySelectorAll('.note-editing-tooltip').forEach((t) => {
      try {
        if ((t as any)._cleanup) {
          (t as any)._cleanup();
        }
        safeRemoveElement(t);
      } catch (error) {
        console.warn('Error removing note tooltip:', error);
      }
    });

    // Trigger mouse leave to close any open tooltips
    const buttons = firstSpan.querySelectorAll('button');
    buttons.forEach((btn) => {
      try {
        btn.dispatchEvent(new MouseEvent('mouseleave', { bubbles: true }));
      } catch (error) {
        console.warn('Error dispatching mouse leave:', error);
      }
    });

    // Remove all spans in the group
    allSpans.forEach((spanToRemove) => {
      const parent = spanToRemove.parentNode;
      if (!parent) return;

      try {
        // Restore original background-color on inner child spans before moving them back,
        // so pre-existing text/bg formatting is not lost when the highlight is removed.
        spanToRemove
          .querySelectorAll<HTMLElement>('[data-original-background-color]')
          .forEach((child) => {
            const originalBg = child.dataset.originalBackgroundColor;
            if (originalBg !== undefined) {
              child.style.backgroundColor = originalBg;
            }
            delete child.dataset.originalBackgroundColor;
            delete child.dataset.highlightId;
          });

        // IMPORTANT: Move all child nodes (including HTML elements like <b>, <strong>, etc.) back to parent
        // This preserves the original HTML structure when removing the highlight
        // Do NOT use textContent as it strips all HTML tags
        while (spanToRemove.firstChild) {
          parent.insertBefore(spanToRemove.firstChild, spanToRemove);
        }

        // Remove the now-empty span safely
        if (spanToRemove.parentNode === parent) {
          parent.removeChild(spanToRemove);
        }

        // Normalize parent to merge adjacent text nodes
        parent.normalize();
      } catch (error) {
        console.warn('Error removing span:', error);
      }
    });
  } catch (error) {
    console.warn('Error removing text highlight:', error);
  }

  // Remove from store
  onUpdate({ id: highlightId }, 'remove');
};

/**
 * 🎨 Update text highlight note UI (minimized note + action buttons)
 *
 * PURPOSE: Create or update UI components (buttons, notes) for a highlight span
 *
 * CRITICAL SAFEGUARD:
 * - NEVER add UI to span group members (data-span-group-member)
 * - ONLY add UI to main spans (data-span-group) or simple spans
 * - This prevents duplicate buttons/notes on cross-element highlights
 *
 * CREATES TWO UI COMPONENTS:
 * 1. Action Buttons (Delete, Add/Edit Note) - Hidden by default, shown on hover
 * 2. Minimized Note Display - Shows abbreviated note text if note exists
 *
 * UI PLACEMENT:
 * - Simple Highlight: UI at default position (top-right of span)
 * - Span Group: UI positioned by CSS and JavaScript to be centered/at edge
 *
 * @param span - The span element to add UI to (must be main span, not member)
 * @param highlight - Highlight data (id, color, note, etc.)
 * @param onUpdate - Callback to update highlight state
 * @param translate - Translation function for i18n
 * @param commentsVisible - Whether comments/notes should be visible
 */
export const updateTextNoteUI = (
  span: HTMLElement,
  highlight: any,
  onUpdate: (highlight: any, action?: string) => void,
  translate: any,
  commentsVisible: boolean
) => {
  try {
    // ========== CRITICAL SAFEGUARD ==========
    // NEVER add UI components to span group members!
    // UI should ONLY exist on the main span (data-span-group) or simple spans
    // This prevents duplicate buttons/notes appearing on every line of a cross-element highlight
    if (span.dataset.spanGroupMember) {
      console.warn('❌ Attempted to add UI to span group member, skipping');
      return;
    }

    // Check if note editor is currently open for this highlight
    const isEditorOpen = document.querySelector(
      `.note-editing-tooltip[data-highlight-id="${highlight.id}"]`
    );

    // Update minimized note only if editor is not open
    const existingNote = span.querySelector('.minimized-note-display');
    if (existingNote) safeRemoveMinimizedNote(existingNote);

    if (highlight.note?.trim() && commentsVisible && !isEditorOpen) {
      createMinimizedNote(span, highlight.note, () => {
        openTextNoteEditor(span, highlight, onUpdate, translate);
      });
    }

    // ALWAYS recreate action buttons to have fresh callbacks
    const existingButtonWrapper = span.querySelector('.highlight-buttons-wrapper');
    if (existingButtonWrapper) {
      // Detach its listeners (incl. the touch-mode document dismiss) first.
      (existingButtonWrapper as any)._cleanup?.();
      safeRemoveElement(existingButtonWrapper);
    }

    // Create fresh buttons with current highlight data
    const hasNote = !!highlight.note?.trim();
    createActionButtons({
      container: span,
      onMessage: () => openTextNoteEditor(span, highlight, onUpdate, translate),
      onRemove: () => {
        console.log('🗑️ Text Remove clicked for:', highlight.id);
        removeTextHighlight(span, highlight.id, onUpdate);
      },
      commentsVisible,
      hasNote,
    });

    span.dataset.hasNote = hasNote ? 'true' : 'false';

    // J21-B099: a highlight whose text content was emptied collapses to zero
    // width, putting the hover-only remove button out of reach. Flag it so the
    // CSS gives it a visible, hoverable footprint (delete stays reachable).
    const groupSpans: HTMLElement[] = span.dataset.spanGroup
      ? [
          span,
          ...Array.from(
            document.querySelectorAll<HTMLElement>(
              `[data-span-group-member="${span.dataset.spanGroup}"]`
            )
          ),
        ]
      : [span];
    const hasVisibleContent = groupSpans.some((s) =>
      Array.from(s.childNodes).some((node) => {
        if (node instanceof HTMLElement) {
          if (isHighlightAccessory(node)) {
            return false;
          }
          return (
            !!node.textContent?.trim() || !!node.querySelector('img') || node.tagName === 'IMG'
          );
        }
        return !!node.textContent?.trim();
      })
    );
    if (hasVisibleContent) {
      delete span.dataset.emptyHighlight;
    } else {
      span.dataset.emptyHighlight = 'true';
    }
  } catch (error) {
    console.warn('Error updating text note UI:', error);
  }
};

/**
 * Open note editor for text highlight
 */
const openTextNoteEditor = (
  span: HTMLElement,
  highlight: any,
  onUpdate: (highlight: any, action?: string) => void,
  translate: any
) => {
  // Get current commentsVisible state
  const commentsVisible = useRealtimeExperienceStore.getState().commentsVisible ?? true;

  // Effect 1 clone-replaces the main span on a note save, detaching this one.
  // The scope container is never cloned, so it stays a valid root.
  const scopeEl = span.closest(HIGHLIGHT_SCOPE_SELECTOR);
  const findLiveMainSpan = () => findMainSpan(scopeEl ?? document, highlight.id);

  // Close any existing note editors first
  document.querySelectorAll('.note-editing-tooltip').forEach((tooltip) => {
    try {
      // Show any hidden minimized notes before removing the editor
      const parentSpan = document.querySelector(
        `[data-highlight-id="${tooltip.getAttribute('data-highlight-id')}"]`
      );
      if (parentSpan) {
        const minimizedNote = parentSpan.querySelector('.minimized-note-display') as HTMLElement;
        if (minimizedNote) {
          minimizedNote.style.display = 'block';
          minimizedNote.style.opacity = '0.5';
        }
      }
      // Clean up event listeners before removing
      if ((tooltip as any)._cleanup) {
        (tooltip as any)._cleanup();
      }
      safeRemoveElement(tooltip);
    } catch (error) {
      console.warn('Error closing existing tooltip:', error);
    }
  });

  // Anchor above line 1's inline box (matches the minimized note). getBoundingClientRect
  // on a line-wrapped span returns the union, which drops the editor at the
  // paragraph corner.
  const computeEditorPosition = () => {
    // A note save re-runs Effect 1, which clone-replaces the span; measuring the
    // detached one drops the open editor into the viewport corner on resize.
    const liveSpan = document.body.contains(span) ? span : findLiveMainSpan() ?? span;
    const firstRect = liveSpan.getClientRects()[0] ?? liveSpan.getBoundingClientRect();
    let x = firstRect.left + firstRect.width / 2 - TOOLTIP_CONFIG.width / 2 + window.scrollX;
    let y = firstRect.top - TOOLTIP_CONFIG.height + TOOLTIP_CONFIG.yOffset + window.scrollY;
    const viewportWidth = window.innerWidth;
    if (x + TOOLTIP_CONFIG.width > viewportWidth + window.scrollX) {
      x = viewportWidth - TOOLTIP_CONFIG.width - 10 + window.scrollX;
    }
    if (x < window.scrollX) x = 10 + window.scrollX;
    if (y < window.scrollY) y = firstRect.bottom + TOOLTIP_CONFIG.fallbackY + window.scrollY;
    return { x, y };
  };
  const position = computeEditorPosition();

  // Hide minimized note while editor is open
  const note = span.querySelector('.minimized-note-display') as HTMLElement;
  if (note) note.style.display = 'none';

  // Track the latest saved note value in closure
  let savedNoteValue = highlight.note || '';

  createNoteEditor({
    position,
    // R2-G56: reanchor from the live span rect on window resize.
    getPosition: computeEditorPosition,
    currentNote: highlight.note || '',
    highlightId: highlight.id,
    onSave: (noteValue) => {
      // Save data to store
      const updated = { ...highlight, note: noteValue };
      onUpdate(updated);
      span.dataset.hasNote = noteValue.trim() ? 'true' : 'false';

      // Track the saved value for use in onClose
      savedNoteValue = noteValue;
    },
    onDelete: () => {
      try {
        // Delete the note
        const updated = { ...highlight, note: '' };
        onUpdate(updated);
        span.dataset.hasNote = 'false';
        savedNoteValue = '';

        // Remove the minimized note
        updateTextNoteUI(span, updated, onUpdate, translate, commentsVisible);

        // Close the tooltip after deleting
        const tooltip = document.querySelector(
          `.note-editing-tooltip[data-highlight-id="${highlight.id}"]`
        );
        if (tooltip) {
          if ((tooltip as any)._cleanup) {
            (tooltip as any)._cleanup();
          }
          safeRemoveElement(tooltip);
        }
      } catch (error) {
        console.warn('Error deleting note:', error);
      }
    },
    onClose: () => {
      try {
        // When editor closes, recreate the minimized note with latest content
        if (savedNoteValue.trim() && commentsVisible) {
          // The captured span is detached if a note save re-ran Effect 1.
          const liveSpan = document.body.contains(span) ? span : findLiveMainSpan();
          if (!liveSpan) return;

          // Remove old minimized note (it might have stale content)
          const existingNote = liveSpan.querySelector('.minimized-note-display');
          if (existingNote) safeRemoveMinimizedNote(existingNote);

          // Create fresh minimized note with current content and event handlers
          const updatedHighlight = { ...highlight, note: savedNoteValue };
          createMinimizedNote(liveSpan, savedNoteValue, () => {
            openTextNoteEditor(liveSpan, updatedHighlight, onUpdate, translate);
          });
        }
      } catch (error) {
        console.warn('Error closing text note editor:', error);
      }
    },
  });
};
